import type { ComponentSpacing, SignageComponent } from '@/config/types';
import { useStableIds } from '@/hooks/useStableIds';
import { componentSpacingVars } from '@/widgets/spacing';
import { WidgetSlot } from '@/widgets/WidgetSlot';

/**
 * The non-slider rendering path: every component in config order, stacked in one column.
 *
 * DOM structure is the legacy `v-else` branch of `moduleLoader.vue`, class for class:
 *
 * ```
 * div.flex.px-0.flex-row.w-full.justify-center.min-w-500
 *   div.main-container                    (width: 100%; padding-inline: 0.5rem)
 *     div.w-full.h-full.flex.flex-col
 *       div.widget-container  × n         (padding from layout.components)
 *         <WidgetSlot />
 * ```
 *
 * It is kept in its own file so SYM-492 adds `SliderLayout.tsx` beside it and switches on it
 * in `ModuleLoader`, rather than restructuring the loader itself.
 *
 * `main-container`'s `padding-inline: 0.5rem` is a hardcoded legacy constant — the only
 * spacing here that does *not* come from config — expressed as `px-2` (0.25rem × 2). The
 * legacy class name is kept as a marker so a DOM diff against the Vue app stays readable.
 */

type FlatLayoutProps = {
  /** Rendered in array order; the index is the stable-id key. */
  components: SignageComponent[];
  spacing: ComponentSpacing;
};

export function FlatLayout({ components, spacing }: FlatLayoutProps) {
  const getId = useStableIds();

  return (
    <div className="flex px-0 flex-row w-full justify-center min-w-500">
      <div className="main-container w-full px-2" style={componentSpacingVars(spacing)}>
        <div className="w-full h-full flex flex-col">
          {components.map((component, index) => {
            // Keyed by the generated id rather than by the index, which legacy used. The two
            // are one-to-one (the id *is* derived from the index) but the id makes the
            // relationship visible in the DOM, and `data-component-id` is what turns a
            // remote console session into something you can correlate with store keys.
            const componentId = getId(index);
            return (
              <div className="widget-container" key={componentId} data-component-id={componentId}>
                <WidgetSlot component={component} componentId={componentId} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
